import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, MapPin, Phone } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useBooking } from "@/context/BookingContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const inr = (n) => "₹" + Number(n).toLocaleString("en-IN");

export default function BookingModal() {
  const { open, setOpen, preselect, services, settings } = useBooking();
  const [form, setForm] = useState({ name: "", phone: "", email: "", service_id: "", date: "", time: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotState, setSlotState] = useState({ loading: false, closed: false, reason: "" });

  useEffect(() => {
    if (open) {
      setConfirmed(null);
      setForm((f) => ({ ...f, service_id: preselect || f.service_id || (services[0]?.id ?? "") }));
    }
  }, [open, preselect, services]);

  useEffect(() => {
    if (!form.date) {
      setSlots([]);
      return;
    }
    setSlotState({ loading: true, closed: false, reason: "" });
    api
      .get("/availability", { params: { date: form.date } })
      .then(({ data }) => {
        setSlots(data.slots || []);
        setSlotState({ loading: false, closed: data.closed, reason: data.reason || "" });
        setForm((f) => (data.slots?.some((s) => s.time === f.time) ? f : { ...f, time: "" }));
      })
      .catch(() => setSlotState({ loading: false, closed: false, reason: "" }));
  }, [form.date]);

  const selected = useMemo(() => services.find((s) => s.id === form.service_id), [services, form.service_id]);
  const price = selected ? (selected.discount_price ?? selected.price) : null;
  const minDate = new Date().toISOString().split("T")[0];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service_id || !form.date || !form.time) {
      toast.error("Please fill your name, phone, service, date and time.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", form);
      setConfirmed(data);
      toast.success("Appointment request received!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Booking failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const wa = settings?.whatsapp || "918240037030";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-lg bg-[#FDFBF7] border border-[#D4AF37]/30 p-0 overflow-hidden gap-0"
        data-testid="booking-modal"
      >
        <DialogTitle className="sr-only">Book an Appointment</DialogTitle>
        <DialogDescription className="sr-only">Select a service, date and time to request an appointment.</DialogDescription>
        {confirmed ? (
          <div className="p-8 text-center" data-testid="booking-confirmation">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200 }}>
              <CheckCircle2 className="mx-auto text-[#3E664E]" size={54} />
            </motion.div>
            <h3 className="font-display text-3xl mt-4 text-[#2A1B1D]">Appointment Requested</h3>
            <p className="text-[#5C4A4D] mt-2 text-sm">We will confirm shortly on your phone / WhatsApp.</p>
            <div className="mt-6 rounded-2xl bg-[#F4EFEA] p-5 text-left space-y-2">
              <Row label="Booking ID" value={confirmed.booking_ref} />
              <Row label="Service" value={confirmed.service_name} />
              <Row label="Date & Time" value={`${confirmed.date} · ${confirmed.time}`} />
              <Row label="Amount" value={inr(confirmed.price_charged)} />
              <Row label="Status" value={confirmed.status} />
            </div>
            <div className="mt-4 text-xs text-[#5C4A4D] flex items-start gap-2 justify-center">
              <MapPin size={14} className="mt-0.5 shrink-0 text-[#8E3B46]" />
              <span>{settings?.address}</span>
            </div>
            <div className="mt-6 flex gap-3">
              <a
                href={`https://wa.me/${wa}?text=${encodeURIComponent(`Hi, I booked ${confirmed.service_name} (${confirmed.booking_ref}) on ${confirmed.date} at ${confirmed.time}.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 rounded-full bg-[#25D366] py-3 text-sm font-medium text-white text-center"
                data-testid="confirm-whatsapp-btn"
              >
                Confirm on WhatsApp
              </a>
              <button
                onClick={() => setOpen(false)}
                className="flex-1 rounded-full border border-[#2A1B1D]/15 py-3 text-sm font-medium text-[#2A1B1D]"
                data-testid="confirm-close-btn"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="booking-form">
            <div className="px-8 pt-8 pb-4 border-b border-[#D4AF37]/20">
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#8E3B46] font-semibold">Reserve your seat</p>
              <h3 className="font-display text-3xl text-[#2A1B1D] mt-1">Book an Appointment</h3>
            </div>

            <div className="px-8 py-6 space-y-4 max-h-[60vh] overflow-y-auto">
              <Field label="Full name">
                <input data-testid="booking-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Phone">
                  <input data-testid="booking-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91 ..." />
                </Field>
                <Field label="Email (optional)">
                  <input data-testid="booking-email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
                </Field>
              </div>
              <Field label="Service">
                <select data-testid="booking-service" value={form.service_id} onChange={set("service_id")} className={inputCls}>
                  <option value="">Select a service</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>
                      {`${s.name} — ${inr(s.discount_price ?? s.price)}`}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Date">
                  <input data-testid="booking-date" type="date" min={minDate} value={form.date} onChange={set("date")} className={inputCls} />
                </Field>
                <Field label="Time slot">
                  <select
                    data-testid="booking-time"
                    value={form.time}
                    onChange={set("time")}
                    disabled={!form.date || slotState.closed || slots.length === 0}
                    className={inputCls}
                  >
                    <option value="">{!form.date ? "Pick a date first" : "Select a slot"}</option>
                    {slots.map((s) => (
                      <option key={s.time} value={s.time} disabled={!s.available}>
                        {`${s.label}${!s.available ? " — booked" : ""}`}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              {form.date && slotState.closed && (
                <p className="text-xs text-[#8E3B46]" data-testid="slot-closed-msg">
                  Closed on this date — {slotState.reason}. Please choose another day.
                </p>
              )}
              <Field label="Notes (optional)">
                <textarea data-testid="booking-notes" value={form.notes} onChange={set("notes")} rows={2} className={inputCls} placeholder="Any preferences..." />
              </Field>
            </div>

            <div className="px-8 py-5 border-t border-[#D4AF37]/20 flex items-center justify-between bg-[#F4EFEA]/60">
              <div>
                <span className="text-xs text-[#5C4A4D] block">Estimated total</span>
                <span className="font-display text-2xl text-[#8E3B46]">{price != null ? inr(price) : "—"}</span>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="booking-submit"
                className="rounded-full bg-[#8E3B46] px-8 py-3 text-sm font-medium text-[#FDFBF7] transition-colors hover:bg-[#722F38] disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Confirm Booking"}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

const inputCls =
  "w-full rounded-xl border border-[#2A1B1D]/12 bg-white/70 px-4 py-2.5 text-sm text-[#2A1B1D] outline-none transition-colors focus:border-[#D4AF37] placeholder:text-[#5C4A4D]/50";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-[#5C4A4D] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-[#5C4A4D]">{label}</span>
      <span className="font-medium text-[#2A1B1D]">{value}</span>
    </div>
  );
}
